import { NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { WidgetDataRequest } from '@compiere-ws/models/widget-data-request';
import { WidgetCenterService } from '@compiere-ws/services/widget-center/widget-center.service';
import { ChartType } from '@iupics-components/models/view-type.enum';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { Global } from '@iupics-manager/models/global-var';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ThemeService } from '@web-desktop/controllers/theme.service';
import { ButtonModule } from 'primeng/button';
import { ChartModule, UIChart } from 'primeng/chart';
import { TooltipModule } from 'primeng/tooltip';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { cloneDeep } from 'lodash';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AbstractWidgetComponent } from '../../abstract-widget.component';
import { WidgetStateComponent } from '../widget-state/widget-state.component';

@Component({
  selector: 'iu-chart-widget',
  templateUrl: './chart-widget.component.html',
  styleUrls: ['./chart-widget.component.scss'],
  standalone: true,
  imports: [ButtonModule, TooltipModule, ChartModule, TranslateModule, WidgetStateComponent, NgTemplateOutlet],
})
export default class ChartWidgetComponent extends AbstractWidgetComponent implements OnInit, AfterViewInit {
  @ViewChild('chart', { read: UIChart })
  chart: UIChart;
  options: any;
  plugins: any;
  chartType: ChartType;
  dataToDisplay: any = null;
  multiDataset: boolean;
  lastLegendDisplayState: boolean;
  lang = 'fr-FR';
  indexAxis = 'x';
  valueColRefId;
  serieColRefId;
  categoryColRefId;
  /** last answer of the server, unchanged (shown again when only colours / format change) */
  #lastResponse: any = null;
  #dataSub: Subscription | undefined;
  #legendKey: string | null = null;
  // legend / labels / open window buttons, rendered by the hosting dashboard card header when headerActionsInFrame
  @ViewChild('headerActions', { static: true }) headerActionsTpl: TemplateRef<unknown>;
  headerActionsInFrame = false;
  /** names under the categories: as the option says, the user may show / hide them (kept on refresh) */
  axisLabelsShown = false;
  #axisLabelsKey: string | null = null;
  constructor(
    private themeService: ThemeService,
    private connectorService: SecurityManagerService,
    private widgetService: WidgetCenterService,
    private translateService: TranslateService,
    uiCreator: UICreatorService
  ) {
    super(uiCreator);
    this.plugins = [this.valueLabelsPlugin];
  }
  updateWidget() {
    const chartJsType = this.getChartJsType(this.widget.widgetType);
    if (chartJsType == null) return;

    // the user's legend toggle survives refreshes; reset when the option or the series column changes
    const legendKey = `${this.displayOpts.legend ?? 'auto'}|${this.widget.serieColumnId ?? 0}`;
    if (legendKey !== this.#legendKey) {
      this.#legendKey = legendKey;
      this.lastLegendDisplayState = this.legendByDefault();
    }
    this.#syncAxisLabels();
    this.chartType = chartJsType;
    this.getDatas();
  }
  getChartJsType(chartType: string): ChartType {
    let chartJsType = null;
    this.indexAxis = 'x';
    switch (chartType) {
      case 'DNC':
        chartJsType = ChartType.DOUGHNUT;
        break;
      case 'PLC':
        chartJsType = ChartType.POLARAREA;
        break;
      case 'RDC':
        chartJsType = ChartType.RADAR;
        break;
      case 'SCC':
        chartJsType = ChartType.SCATTER;
        break;
      case 'BUC':
        chartJsType = ChartType.BUBBLE;
        break;
      case 'PIC':
        chartJsType = ChartType.PIE;
        break;
      case 'LIC':
        chartJsType = ChartType.LINE;
        break;
      case 'CLC':
        chartJsType = ChartType.BAR;
        break;
      case 'BRC':
        chartJsType = ChartType.BAR;
        this.indexAxis = 'y';
        break;
      default:
        chartJsType = null;
        break;
    }
    return chartJsType;
  }
  ngOnInit() {
    this.lang = this.connectorService.getIupicsDefaultLanguage().iso_code.replace('_', '-');
    if (this.resizeWidgetEmitter)
      this.subscriptions.push(
        this.resizeWidgetEmitter.subscribe(() => {
          if (this.chart && this.options) {
            this.adaptOptionsWithScreen();
          }
        })
      );
  }

  override ngOnDestroy(): void {
    this.#dataSub?.unsubscribe();
    super.ngOnDestroy();
  }

  /** colours, unit, precision changed: the data already read is drawn again (no new request) */
  override applyPresentation(widget: IupicsWidget): void {
    this.setPresentation(widget);
    const legendKey = `${this.displayOpts.legend ?? 'auto'}|${widget?.serieColumnId ?? 0}`;
    if (legendKey !== this.#legendKey) {
      this.#legendKey = legendKey;
      this.lastLegendDisplayState = this.legendByDefault();
    }
    this.#syncAxisLabels();
    if (this.#lastResponse && this.chartType) {
      this.#show(this.#lastResponse);
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // check si on est sur mobile ou si le widget est trop petit pour afficher la légende
    if (this.dataToDisplay) {
      this.adaptOptionsWithScreen();
    }
  }

  /** coefficient, then rounded or truncated to the precision (same rule as the count) */
  parseAndRound(value) {
    return this.formatValue(value);
  }
  getDatas() {
    this.valueColRefId = null;
    this.serieColRefId = null;
    this.categoryColRefId = null;
    if (
      (this.widget.tabId || this.widget.tableId) &&
      this.widget.aggregateColumnId &&
      this.widget.aggregateOperator &&
      this.widget.categoryColumnId
    ) {
      const request: WidgetDataRequest = { widget: this.widget };
      this.isLoading = true;
      // a newer request wins (the editor preview asks again while settings change)
      this.#dataSub?.unsubscribe();
      this.#dataSub = this.widgetService
        .getWidgetDatas(request)
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          // null = HTTP error; an empty response (no rows) has no chartData; noAccess: the role cannot read it
          this.loadError = !res;
          this.noAccess = !!res?.noAccess;
          if (res && !this.noAccess) this.markUpdated();
          this.#lastResponse = res;
          this.#show(res);
          this.isLoading = false;
        });
    } else {
      this.#dataSub?.unsubscribe();
      this.#lastResponse = null;
      this.isLoading = false;
      this.loadError = false;
      this.dataToDisplay = null;
      this.lastLegendDisplayState = false;
      this.#legendKey = null;
      this.axisLabelsShown = false;
      this.#axisLabelsKey = null;
      this.chartType = null;
    }
  }

  /** draws an answer of the server (a copy: the values are formatted in place) */
  #show(response: any) {
    const res = cloneDeep(response);
    if (res?.chartData?.datasets?.length > 0) {
      this.serieColRefId = res?.serieColRefId;
      this.categoryColRefId = res?.categoryColRefId;
      this.valueColRefId = res?.valueColRefId;
      // running total (line charts): summed on the values read, then formatted
      if (this.displayOpts.cumulative && this.chartType === ChartType.LINE) {
        for (const ds of res.chartData.datasets) {
          let total = 0;
          ds.data = ds.data.map((v) => {
            const n = parseFloat(v);
            total += isFinite(n) ? n : 0;
            return total;
          });
        }
      }
      for (const ds of res.chartData.datasets) {
        ds.data = ds.data.map((line) => {
          const prop = this.getValueAxe();
          if (line[prop] != undefined) {
            if (line[prop] != 0) line[prop] = this.parseAndRound(line[prop]);
          } else {
            if (line != 0) line = this.parseAndRound(line);
          }

          return line;
        });
      }
      this.multiDataset =
        this.chartType === ChartType.POLARAREA ||
        this.chartType === ChartType.RADAR ||
        this.chartType === ChartType.SCATTER ||
        this.chartType === ChartType.BUBBLE;
      if (this.multiDataset && res.chartData?.datasets?.length <= 1) {
        this.dataToDisplay = null;
      } else {
        this.dataToDisplay = res.chartData;
      }
    } else {
      this.dataToDisplay = null;
    }
    if (this.dataToDisplay) {
      const isOfChartType = [ChartType.AREA, ChartType.BAR, ChartType.COLUMN, ChartType.RADAR].includes(
        this.chartType
      );
      for (const dataset of this.dataToDisplay.datasets) dataset['fill'] = isOfChartType;

      const palette: any[] = this.themeService.getThemeProperty('colorsPalette') ?? [];
      if (this.widget.serieColumnId) {
        for (let i = 0; i < (this.dataToDisplay.datasets.length ?? 0); i++) {
          const dataset = this.dataToDisplay.datasets[i];
          const color = i < palette.length ? palette[i] : this.extraColor(i);
          const _opacity = Math.round(0.7 * 255);
          const backgroundColor = color + _opacity.toString(16).toUpperCase();
          dataset.borderColor = color;
          dataset.pointBorderColor = '#fff';
          dataset.pointBackgroundColor = color;
          dataset.backgroundColor = backgroundColor;
          dataset.hoverBackgroundColor = color;
        }
      } else if (this.dataToDisplay.datasets.length > 0) {
        for (let i = 0; i < (this.dataToDisplay.labels.length ?? 0); i++) {
          const color = i < palette.length ? palette[i] : this.extraColor(i);
          const _opacity = Math.round(0.7 * 255);
          const backgroundColor = color + _opacity.toString(16).toUpperCase();
          if (!this.dataToDisplay.datasets[0].backgroundColor) {
            this.dataToDisplay.datasets[0].backgroundColor = [];
          }
          this.dataToDisplay.datasets[0].backgroundColor.push(backgroundColor);
          if (!this.dataToDisplay.datasets[0].hoverBackgroundColor) {
            this.dataToDisplay.datasets[0].hoverBackgroundColor = [];
          }
          this.dataToDisplay.datasets[0].hoverBackgroundColor.push(color);
          this.dataToDisplay.datasets[0].borderColor = '#fff';
          this.dataToDisplay.datasets[0].pointBorderColor = '#fff';
          this.dataToDisplay.datasets[0].pointBackgroundColor = color;
        }
        if (this.chartType === ChartType.LINE) {
          // one line: drawn in the first colour (white borders made it invisible)
          const first = palette[0] ?? this.extraColor(0);
          this.dataToDisplay.datasets[0].borderColor = first;
          this.dataToDisplay.datasets[0].pointBorderColor = first;
        }
      }

      this.options = {
        responsive: true,
        maintainAspectRatio: false,

        plugins: {
          title: {
            display: !!this.widget.description,
            font: { size: 12 },
            text: this.widget.description ?? '',
          },
          tooltip: {
            callbacks: {
              title: (ctx, idx) => {
                if (this.serieColRefId) {
                  return this.getFormatFromReference(ctx[0].label, this.serieColRefId);
                } else {
                  return ctx[0].dataset.label;
                }
              },
              label: (data) => {
                let dataSetValue: any = null;
                if ([ChartType.BUBBLE, ChartType.SCATTER].includes(this.chartType as ChartType)) {
                  dataSetValue = data.dataset.data[data.dataIndex].x;
                } else {
                  dataSetValue = data.dataset.data[data.dataIndex];
                }
                const shown = this.display(Number(dataSetValue), this.lang);
                if (this.serieColRefId) {
                  return `${this.getFormatFromReference(data?.dataset?.label, this.categoryColRefId)} : ${shown}`;
                } else {
                  return `${this.getFormatFromReference(data?.label, this.categoryColRefId)} : ${shown}`;
                }
              },
            },
          },
          legend: {
            // kept when the chart is drawn again with other colours / format
            display: this.lastLegendDisplayState,
          },
          apizValueLabels: { display: !!this.displayOpts.showValues },
        },
        scales: this.#scales(),
      };
      if (this.chartType === ChartType.BAR) {
        this.options.indexAxis = this.indexAxis;
      }
      setTimeout(() => {
        if (this.chart) this.chart.reinit();
      }, 10);
    }
  }

  /** legend: as chosen; by default shown when there are series (one colour per series to explain) */
  private legendByDefault(): boolean {
    const legend = this.displayOpts.legend ?? 'auto';
    return legend === 'show' || (legend === 'auto' && this.widget?.serieColumnId > 0);
  }

  /** axes: ticks of getAxesScale, piled-up series, axis titles (value axis horizontal for horizontal bars) */
  #scales(): any {
    const scales: any = this.getAxesScale() ?? {};
    const cartesian = [ChartType.BAR, ChartType.LINE, ChartType.SCATTER, ChartType.BUBBLE].includes(this.chartType);
    if (!cartesian) return scales;
    for (const axis of ['x', 'y']) scales[axis] = scales[axis] ?? {};
    if (this.displayOpts.stacked && [ChartType.BAR, ChartType.LINE].includes(this.chartType)) {
      scales.x.stacked = true;
      scales.y.stacked = true;
      // the piled-up totals go beyond the single values: let Chart.js size the value axis
      const valueAxis = this.chartType === ChartType.BAR && this.indexAxis === 'y' ? 'x' : 'y';
      delete scales[valueAxis].min;
      delete scales[valueAxis].max;
    }
    // category names: hidden unless asked (they took the room of the bars; the tooltip gives them)
    if ([ChartType.BAR, ChartType.LINE].includes(this.chartType)) {
      const categoryAxis = this.chartType === ChartType.BAR && this.indexAxis === 'y' ? 'y' : 'x';
      scales[categoryAxis].ticks = { ...(scales[categoryAxis].ticks ?? {}), display: this.axisLabelsShown };
    }
    // titles of the horizontal and vertical axes (as seen, whatever the chart puts on them)
    if (this.displayOpts.xTitle) scales.x.title = { display: true, text: this.displayOpts.xTitle };
    if (this.displayOpts.yTitle) scales.y.title = { display: true, text: this.displayOpts.yTitle };
    return scales;
  }

  /** draws each value on its bar, point or slice when the widget asks for it (plugins.apizValueLabels.display) */
  readonly valueLabelsPlugin = {
    id: 'apizValueLabels',
    afterDatasetsDraw: (chart: any) => {
      if (!chart?.options?.plugins?.apizValueLabels?.display) return;
      const ctx = chart.ctx;
      const horizontal = chart.options.indexAxis === 'y';
      const bars = this.chartType === ChartType.BAR;
      const slices = [ChartType.PIE, ChartType.DOUGHNUT, ChartType.POLARAREA].includes(this.chartType);
      const points = [ChartType.SCATTER, ChartType.BUBBLE].includes(this.chartType);
      ctx.save();
      ctx.font = '11px sans-serif';
      ctx.fillStyle = getComputedStyle(chart.canvas).color || '#333';
      ctx.textAlign = horizontal && bars ? 'left' : 'center';
      ctx.textBaseline = 'middle';
      chart.data.datasets.forEach((ds: any, i: number) => {
        const meta = chart.getDatasetMeta(i);
        if (meta.hidden) return;
        meta.data.forEach((el: any, j: number) => {
          const raw = ds.data[j];
          const v = raw !== null && typeof raw === 'object' ? (points ? (raw.x ?? raw.y) : (raw.y ?? raw.x ?? raw.r)) : raw;
          if (v === null || v === undefined || Number(v) === 0) return;
          const p = el.tooltipPosition();
          const x = horizontal && bars ? p.x + 4 : p.x;
          const y = slices || (horizontal && bars) ? p.y : p.y - 8;
          ctx.fillText(this.display(Number(v), this.lang), x, y);
        });
      });
      ctx.restore();
    },
  };

  /** colour after the theme palette: spread around the wheel, the same on every refresh (was random) */
  private extraColor(index: number): string {
    const hue = Math.round((index * 137.508) % 360);
    return this.hslToHex(hue, 55, 50);
  }

  private hslToHex(h: number, s: number, l: number): string {
    const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const c = l / 100 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * c)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
  }

  /** bars, columns, lines: the chart has a category axis */
  get hasCategoryAxis(): boolean {
    return this.chartType === ChartType.BAR || this.chartType === ChartType.LINE;
  }

  /** the option changed: back to what it says */
  #syncAxisLabels(): void {
    const key = String(!!this.displayOpts.axisLabels);
    if (key !== this.#axisLabelsKey) {
      this.#axisLabelsKey = key;
      this.axisLabelsShown = !!this.displayOpts.axisLabels;
    }
  }

  toggleAxisLabels(): void {
    this.axisLabelsShown = !this.axisLabelsShown;
    if (this.options) {
      this.options.scales = this.#scales();
      this.chart?.reinit();
    }
  }

  // Permet d'afficher ou cacher la légende
  changeLegendView() {
    this.options.plugins.legend.display = !this.options.plugins.legend.display;
    this.lastLegendDisplayState = !this.lastLegendDisplayState;
    this.chart.reinit();
  }

  adaptOptionsWithScreen() {
    if (this.chart.el?.nativeElement.children[0].clientWidth <= 400 || Global.getDeviceWidth() <= 640) {
      if (this.options.plugins?.title) this.options.plugins.title.font = { size: 11 };
      if (this.options.plugins.legend) this.options.plugins.legend.position = 'right';
    } else {
      if (this.options.plugins?.title) this.options.plugins.title.font = { size: 12 };
      if (this.options.plugins.legend) this.options.plugins.legend.position = 'top';
    }
    if (this.lastLegendDisplayState !== this.options.plugins.legend.display) {
      this.lastLegendDisplayState = !this.lastLegendDisplayState;
      this.chart.reinit();
    }
  }
  getValueAxe() {
    if (ChartType.RADAR === this.chartType) {
      return 'r';
    }

    if (
      (this.indexAxis === 'y' && [ChartType.BAR].includes(this.chartType)) ||
      [ChartType.SCATTER, ChartType.BUBBLE].includes(this.chartType)
    ) {
      return 'x';
    }

    if (
      (this.indexAxis === 'x' && [ChartType.COLUMN, ChartType.BAR].includes(this.chartType)) ||
      [ChartType.LINE, ChartType.SCATTER, ChartType.AREA, ChartType.BUBBLE].includes(this.chartType)
    ) {
      return 'y';
    }

    return 'r';
  }
  getAxesScale() {
    if (ChartType.RADAR === this.chartType) {
      return {};
    }

    const axesScale = {};
    // has x axe
    if (
      (this.indexAxis === 'y' && [ChartType.BAR].includes(this.chartType)) ||
      [ChartType.SCATTER, ChartType.BUBBLE].includes(this.chartType)
    ) {
      const allDataForXAxe: number[] = [];
      for (const ds of this.dataToDisplay.datasets) {
        for (const line of ds.data) {
          let value = line.x ?? line;
          if (!allDataForXAxe.includes(value)) {
            allDataForXAxe.push(value);
          }
        }
      }
      axesScale['x'] = this.getTicks(allDataForXAxe);
      axesScale['y'] = this.getTicks();
    }

    // column charts are bars on the x index (CLC): formatted like the others
    if (
      (this.indexAxis === 'x' && [ChartType.COLUMN, ChartType.BAR].includes(this.chartType)) ||
      [ChartType.LINE, ChartType.SCATTER, ChartType.AREA, ChartType.BUBBLE].includes(this.chartType)
    ) {
      // has y axe
      let allDataForYAxe = [];
      for (const ds of this.dataToDisplay.datasets) {
        for (const line of ds.data) {
          const value = line.y ?? line;
          if (!allDataForYAxe.includes(value)) {
            allDataForYAxe.push(value);
          }
        }
      }
      axesScale['y'] = this.getTicks(allDataForYAxe);
      axesScale['x'] = this.getTicks();
    }

    return axesScale;
  }
  getTicks(data?: number[]) {
    if (!data) {
      return {
        ticks: {
          callback: (value, index, values) => {
            let displayValue = this.getFormatFromReference(
              this.dataToDisplay.labels[index],
              this.serieColRefId ?? this.categoryColRefId
            );
            if (displayValue) {
              displayValue = displayValue.split(/[ _]/);
            }

            return displayValue;
          },
        },
      };
    }
    const max = Math.max(...data);
    const min = Math.min(...data);
    const [order, mag] = this.getOrderOfMagnitude(Math.abs(max) + Math.abs(min));
    let stepSize = mag;
    if (order > 0 && order < 3) {
      stepSize *= 2.5;
    } else if (order < 4) {
      stepSize /= 4;
    }
    return {
      min: min >= 0 ? 0 : min + (Math.abs(min) % stepSize) - stepSize * 2,
      max: max <= 0 ? 0 : max - (Math.abs(max) % stepSize) + stepSize * 2,
      ticks: {
        stepSize,
        callback: (value, index, values) => {
          return this.display(Number(value), this.lang);
        },
      },
    };
  }
  getOrderOfMagnitude(n: number) {
    // From: https://stackoverflow.com/questions/23917074/javascript-flooring-number-to-order-of-magnitude
    const order = Math.floor(Math.log(Math.abs(n)) / Math.LN10 + 0.000000001);
    return [order, Math.pow(10, order)];
  }

  private getFormatFromReference(value: any, referenceId: number) {
    if (value !== null && value !== undefined && referenceId) {
      return referenceId === 20
        ? value.toLowerCase() === 'y'
          ? this.translateService.instant('widgetEditor.yes')
          : this.translateService.instant('widgetEditor.no')
        : referenceId === 15 || referenceId === 16 || referenceId === 24
          ? this.parseDate(value, referenceId)
          : referenceId === 12 || referenceId === 37 || referenceId === 22 || referenceId === 29 || referenceId === 11
            ? value
            : value;
    } else {
      return value;
    }
  }

  private parseDate(value: any, referenceId: number) {
    let date;
    try {
      if (referenceId === 15) {
        /* Display Type 15	Date	*/
        date = new Date(value).toLocaleDateString();
      } else if (referenceId === 16) {
        /* Display Type 16	DateTime	*/
        date = new Date(value).toLocaleString();
      } else if (referenceId === 24) {
        /* Display Type 24	Time	*/
        date = new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    } catch (error) {
      date = value;
    }
    return date;
  }
}
